import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _get from 'lodash/get';
import _uniqBy from 'lodash/uniqBy';
import { GetLoadOptions, LoadOptionsParams, SelectOption } from '../types';

type Params = {
  getLoadOptions?: GetLoadOptions;
  name?: string;
  options?: SelectOption[];
  values?: Record<string, SelectOption[]>;
};

type NormalizedResponse = {
  options: SelectOption[];
  hasMore: boolean;
  additional: LoadOptionsParams;
};

const defaultAdditional: LoadOptionsParams = { page: 1 };

export default function useSelectPanelOptions({
  getLoadOptions,
  name,
  options = [],
  values,
}: Params) {
  const hasAsyncSource = Boolean(getLoadOptions);
  const [preparedOptions, setPreparedOptions] =
    useState<SelectOption[]>(options);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);
  const additionalRef = useRef<LoadOptionsParams>(defaultAdditional);
  const itemsRef = useRef<SelectOption[]>(options);
  const requestIdRef = useRef(0);
  const isLoadingRef = useRef(false);

  const loadOptionsFn = useMemo(() => {
    if (getLoadOptions) {
      return getLoadOptions(name ?? '', values);
    }
    return undefined;
  }, [getLoadOptions, name, values]);

  const isAsync = hasAsyncSource;

  useEffect(() => {
    if (!isAsync) {
      itemsRef.current = options;
      setPreparedOptions(options);
      setHasMore(false);
    }
  }, [isAsync, options]);

  useEffect(() => {
    if (!isAsync) return;
    itemsRef.current = [];
    setPreparedOptions([]);
    setHasMore(false);
    isLoadingRef.current = false;
    setIsLoading(false);
    additionalRef.current = defaultAdditional;
    setResetCounter((value) => value + 1);
  }, [isAsync, name]);

  const normalizeResponse = useCallback(
    (response: unknown, reset: boolean): NormalizedResponse => {
      if (!response) {
        return {
          options: reset ? [] : itemsRef.current,
          hasMore: false,
          additional: { page: 1 },
        };
      }
      if (Array.isArray(response)) {
        return {
          options: response,
          hasMore: false,
          additional: reset ? { page: 1 } : additionalRef.current,
        };
      }
      const data = response as NormalizedResponse;
      return {
        options: (data.options as SelectOption[]) ?? [],
        hasMore: Boolean(data.hasMore),
        additional: data.additional ?? { page: 1 },
      };
    },
    [],
  );

  const runRequest = useCallback(
    async (searchQuery: string, reset: boolean) => {
      if (!loadOptionsFn) {
        return;
      }
      // Prevent concurrent requests
      if (isLoadingRef.current && !reset) {
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      isLoadingRef.current = true;
      setIsLoading(true);
      const params = reset ? { page: 1 } : additionalRef.current;
      const loaded = reset ? [] : itemsRef.current;
      try {
        const response = await loadOptionsFn(searchQuery, loaded, params);
        if (requestIdRef.current !== requestId) return;
        const normalized = normalizeResponse(response, reset);
        const mergedOptions = reset
          ? normalized.options
          : _uniqBy(
              [...itemsRef.current, ...normalized.options],
              (option) => option?.id ?? _get(option, 'value'),
            );
        itemsRef.current = mergedOptions;
        setPreparedOptions(mergedOptions);
        setHasMore(Boolean(normalized.hasMore));
        additionalRef.current = normalized.additional ?? { page: 1 };
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [loadOptionsFn, normalizeResponse],
  );

  const loadInitial = useCallback(
    (searchQuery: string) => runRequest(searchQuery, true),
    [runRequest],
  );

  const loadMore = useCallback(
    (searchQuery: string) => runRequest(searchQuery, false),
    [runRequest],
  );

  return {
    hasMore,
    isAsync,
    isLoading,
    resetCounter,
    loadInitial,
    loadMore,
    options: isAsync ? preparedOptions : options,
  };
}
